/**
 * The money maths for flipping one car. Pure functions, no UI.
 * All amounts are pounds. Every figure is an estimate the person types in.
 */

export interface CostLine {
  id: string;
  label: string;
  amount: number;
}

/** Everything that decides whether one car makes money. */
export interface Deal {
  purchasePrice: number;
  /** Auction buyer's premium, as a percentage of the purchase price. */
  buyerFeePct: number;
  /** Fixed buying fees (auction entry, admin). */
  buyerFeeFixed: number;
  /** Fuel, transport or recovery to get the car home. */
  collection: number;
  /** Repairs and parts, one line each. */
  repairs: CostLine[];
  /** Extra allowance for repairs that turn out worse than expected, in percent. */
  contingencyPct: number;
  mot: number;
  valet: number;
  /** HPI or similar history check. */
  historyCheck: number;
  /** Road tax (VED) paid while you hold the car. */
  tax: number;
  insurancePerDay: number;
  storagePerDay: number;
  /** Listing or advertising fee. */
  adFee: number;
  salePrice: number;
  /** Selling platform and payment fees, as a percentage of the sale price. */
  saleFeePct: number;
  /** Days from buying to cash in the bank. */
  holdingDays: number;
  /** What tied-up cash costs you per year (finance interest or what else it could earn), in percent. */
  capitalCostPctPerYear: number;
}

/** What you need a deal to deliver to be worth doing. */
export interface Targets {
  minProfit: number;
  minRoiPct: number;
  minProfitPerDay: number;
}

export type Verdict = 'good' | 'marginal' | 'avoid';

export interface Result {
  buyerFees: number;
  repairsSubtotal: number;
  contingency: number;
  repairsTotal: number;
  /** Insurance and storage over the holding period. */
  runningCosts: number;
  /** Fixed extras: MOT, valet, history check, tax, advert, collection. */
  otherCosts: number;
  /** Cash you have to put in before selling (the most money at risk). */
  cashInvested: number;
  saleFees: number;
  capitalCost: number;
  totalCosts: number;
  netSale: number;
  profit: number;
  /** Profit as a percentage of cash invested. */
  roiPct: number;
  profitPerDay: number;
  /** Profit on the sale price, in percent. */
  marginPct: number;
  /** The lowest sale price that still breaks even. */
  breakEvenSalePrice: number;
}

export const DEFAULT_TARGETS: Targets = { minProfit: 400, minRoiPct: 20, minProfitPerDay: 15 };

export const emptyDeal = (): Deal => ({
  purchasePrice: 0,
  buyerFeePct: 0,
  buyerFeeFixed: 0,
  collection: 0,
  repairs: [],
  contingencyPct: 15,
  mot: 0,
  valet: 25,
  historyCheck: 20,
  tax: 0,
  insurancePerDay: 3,
  storagePerDay: 0,
  adFee: 0,
  salePrice: 0,
  saleFeePct: 0,
  holdingDays: 21,
  capitalCostPctPerYear: 0,
});

const money = (n: number) => (Number.isFinite(n) ? n : 0);
const nonNeg = (n: number) => Math.max(0, money(n));

/** Work out what a deal costs and earns. */
export function evaluate(d: Deal): Result {
  const days = Math.max(1, Math.round(nonNeg(d.holdingDays)));
  const purchase = nonNeg(d.purchasePrice);
  const buyerFees = (purchase * nonNeg(d.buyerFeePct)) / 100 + nonNeg(d.buyerFeeFixed);
  const repairsSubtotal = d.repairs.reduce((s, r) => s + nonNeg(r.amount), 0);
  const contingency = (repairsSubtotal * nonNeg(d.contingencyPct)) / 100;
  const repairsTotal = repairsSubtotal + contingency;
  const runningCosts = days * (nonNeg(d.insurancePerDay) + nonNeg(d.storagePerDay));
  const otherCosts =
    nonNeg(d.mot) + nonNeg(d.valet) + nonNeg(d.historyCheck) + nonNeg(d.tax) +
    nonNeg(d.adFee) + nonNeg(d.collection);
  const cashInvested = purchase + buyerFees + repairsTotal + otherCosts;
  const capitalCost = (cashInvested * nonNeg(d.capitalCostPctPerYear) * (days / 365)) / 100;
  const saleFees = (nonNeg(d.salePrice) * nonNeg(d.saleFeePct)) / 100;
  const totalCosts = cashInvested + runningCosts + capitalCost + saleFees;
  const netSale = nonNeg(d.salePrice) - saleFees;
  const profit = nonNeg(d.salePrice) - totalCosts;
  const costsBeforeSaleFees = cashInvested + runningCosts + capitalCost;
  const feeShare = Math.min(nonNeg(d.saleFeePct), 99.99) / 100;
  return {
    buyerFees, repairsSubtotal, contingency, repairsTotal, runningCosts, otherCosts,
    cashInvested, saleFees, capitalCost, totalCosts, netSale, profit,
    roiPct: cashInvested > 0 ? (profit / cashInvested) * 100 : 0,
    profitPerDay: profit / days,
    marginPct: nonNeg(d.salePrice) > 0 ? (profit / nonNeg(d.salePrice)) * 100 : 0,
    breakEvenSalePrice: costsBeforeSaleFees / (1 - feeShare),
  };
}

/**
 * The most you can pay for the car and still reach `targetProfit`.
 * Profit falls in a straight line as the price rises, so two evaluations are enough.
 * Returns 0 when even a free car would not reach the target.
 */
export function maxPurchasePrice(d: Deal, targetProfit: number): number {
  const at0 = evaluate({ ...d, purchasePrice: 0 }).profit;
  const at1000 = evaluate({ ...d, purchasePrice: 1000 }).profit;
  const slope = (at1000 - at0) / 1000;
  if (slope >= 0) return 0;
  return Math.max(0, Math.floor((targetProfit - at0) / slope));
}

/** Is the deal worth doing against the person's own targets? */
export function verdict(r: Result, t: Targets): Verdict {
  const hits = [r.profit >= t.minProfit, r.roiPct >= t.minRoiPct, r.profitPerDay >= t.minProfitPerDay];
  if (r.profit <= 0) return 'avoid';
  const n = hits.filter(Boolean).length;
  return n === 3 ? 'good' : n >= 1 ? 'marginal' : 'avoid';
}

/** How a plan could play out: slower, dearer and cheaper to sell than hoped, or better. */
export interface ScenarioSettings {
  cautious: { salePricePct: number; repairsPct: number; extraDays: number };
  good: { salePricePct: number; repairsPct: number; extraDays: number };
}

export const DEFAULT_SCENARIOS: ScenarioSettings = {
  cautious: { salePricePct: -6, repairsPct: 30, extraDays: 14 },
  good: { salePricePct: 3, repairsPct: -10, extraDays: -5 },
};

export type ScenarioName = 'cautious' | 'expected' | 'good';

/** Apply a scenario to a deal. */
export function applyScenario(d: Deal, name: ScenarioName, s: ScenarioSettings = DEFAULT_SCENARIOS): Deal {
  if (name === 'expected') return d;
  const x = s[name];
  return {
    ...d,
    salePrice: d.salePrice * (1 + x.salePricePct / 100),
    repairs: d.repairs.map((r) => ({ ...r, amount: r.amount * (1 + x.repairsPct / 100) })),
    holdingDays: Math.max(3, d.holdingDays + x.extraDays),
  };
}

export interface ScenarioResults {
  cautious: Result;
  expected: Result;
  good: Result;
}

export function scenarios(d: Deal, s: ScenarioSettings = DEFAULT_SCENARIOS): ScenarioResults {
  return {
    cautious: evaluate(applyScenario(d, 'cautious', s)),
    expected: evaluate(d),
    good: evaluate(applyScenario(d, 'good', s)),
  };
}

/** Round to pence for display and comparison. */
export const pence = (n: number): number => Math.round(money(n) * 100) / 100;
